/**
 * GenericEntityContent — schema-driven content card for CREATED entities that
 * have no bespoke registered card (i.e. custom `org_*` entity types).
 *
 * Replaces the bare icon+label EntityCard fallback: it renders the entity's
 * display name plus its top few properties, chosen from the entity type's
 * property schema via the same `selectInlineFields` heuristic used by the
 * pipeline board cards (custom `org_*` fields first, then field-type priority),
 * and formatted with the shared `PropertyValueLabel`. Values come straight from
 * the proposed action payload (`flatProps`), so a not-yet-created entity still
 * shows its fields.
 *
 * Field selection is heuristic today; making it user-configurable (reusing the
 * `ahoy_card_config` system) is a planned fast-follow.
 */

import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useMemo,
} from 'react';

import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { ObjectCard } from '@/lib/crm/object-cards/ObjectCard';
import { selectInlineFields } from '@/lib/crm/pipeline/utils/cardConfig';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { cx } from '@/utils/cx';

interface GenericEntityContentProps {
  /** CRM entity type name (e.g. 'org_bug'). */
  entityType: string;
  /** Display label for the entity (already resolved by the caller). */
  label: string;
  /** Proposed property values from the create action payload (extracted/flat). */
  flatProps: Record<string, unknown>;
  /** Org id — scopes the property-schema fetch. */
  orgId: number | undefined;
  /** Max fields to show in the value row. Defaults to 4. */
  maxFields?: number;
  /**
   * Injected by CrmClickableContent for in-CRM entities — makes the display
   * name open the CRM slideout (matches the registered card contract, e.g.
   * DealCardContent). Absent for not-yet-created entities.
   */
  onClick?: () => void;
}

export function GenericEntityContent({
  entityType,
  label,
  flatProps,
  orgId,
  maxFields = 4,
  onClick,
}: GenericEntityContentProps) {
  // orgId 0 is usePropertySchema's "don't fetch" sentinel — the card then
  // degrades to just the name, same as the old EntityCard fallback.
  const { schema } = usePropertySchema(orgId ?? 0, entityType);
  const Icon = getEntityTypeIconComponent(entityType);

  const fields = useMemo(() => {
    // Over-select, then keep only SHORT, single-line values — long/multi-line
    // fields (e.g. a markdown repro or description) would blow up the compact
    // card; their full content is available on the record. Then cap at maxFields.
    return selectInlineFields(schema, 8)
      .map(propertyType => ({
        propertyType,
        value: flatProps[propertyType.property_type_name],
      }))
      .filter(({ propertyType, value }) => {
        if (value === null || value === undefined || value === '') {
          return false;
        }
        const str = Array.isArray(value) ? value.join(', ') : String(value);
        return (
          propertyType.field_type_name !== 'markdown' &&
          str.trim() !== '' &&
          !str.includes('\n') &&
          str.length <= 80
        );
      })
      .slice(0, maxFields);
  }, [schema, maxFields, flatProps]);

  const handleNameClick = useCallback(
    (e: MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const handleNameKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <ObjectCard bordered>
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Icon
            className='w-4 h-4 text-fg-secondary shrink-0'
            aria-hidden='true'
          />
          <p
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick ? handleNameClick : undefined}
            onKeyDown={onClick ? handleNameKeyDown : undefined}
            className={cx(
              'text-sm font-semibold text-fg-primary truncate',
              onClick &&
                'cursor-pointer hover:underline hover:text-fg-brand-primary'
            )}
          >
            {label}
          </p>
        </div>

        {fields.length > 0 && (
          <div className='flex flex-wrap gap-x-4 gap-y-1'>
            {fields.map(({ propertyType, value }) => (
              <div
                key={propertyType.property_type_name}
                className='flex items-center gap-1.5 min-w-0'
              >
                <span className='text-xs text-fg-tertiary shrink-0'>
                  {propertyType.label}
                </span>
                <span className='text-sm text-fg-secondary truncate min-w-0'>
                  <PropertyValueLabel
                    value={value}
                    propertyType={propertyType}
                    fontSize='sm'
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </ObjectCard>
  );
}
